import { useState } from "react";
import {
  Table,
  Button,
  Drawer,
  Form,
  Input,
  Select,
  Switch,
  Tag,
  Popconfirm,
  Space,
  Typography,
  Avatar,
} from "antd";
import { OLIVEEB_COLORS } from "utils/colors";
import useWebsiteTemplates from "hooks/web-templates/useWebsiteTemplates";

const { TextArea } = Input;
const { Text } = Typography;

const TYPE_OPTIONS = [
  { value: "BUSINESS", label: "Business" },
  { value: "PORTFOLIO", label: "Portfolio" },
];

const CATEGORY_OPTIONS = [
  { value: "E-commerce", label: "E-commerce" },
  { value: "Landing page", label: "Landing page" },
  { value: "Courses & Coaching", label: "Courses & Coaching" },
  { value: "Education", label: "Education" },
  { value: "Marketing & Advertising", label: "Marketing & Advertising" },
  { value: "Construction & Interior", label: "Construction & Interior" },
  { value: "Sport & Lifestyle", label: "Sport & Lifestyle" },
  { value: "Business & IT", label: "Business & IT" },
  {value: "Homecare Services", label: "Homecare Services"},
  { value: "Restaurant & Food", label: "Restaurant & Food" },
  { value: "Portfolio & CV", label: "Portfolio & CV" },
  { value: "Medical & health", label: "Medical & health" },
  { value: "Fashion & Beauty", label: "Fashion & Beauty" },
  { value: "Nonprofit & Church", label: "Nonprofit & Church" },
  { value: "Home & Gardening", label: "Home & Gardening" },
  { value: "Legal & Finance", label: "Legal & Finance" },
  { value: "Travel & Tourism", label: "Travel & Tourism" },
  { value: "Art & Design", label: "Art & Design" },
  {
    value: "Agriculture & Manufacturing",
    label: "Agriculture & Manufacturing",
  },
  { value: "Pets & Animals", label: "Pets & Animals" },
  {
    value: "Automotive & transportation",
    label: "Automotive & transportation",
  },
  { value: "Real Estate", label: "Real Estate" },
  { value: "Wedding", label: "Wedding" },
  { value: "Events", label: "Events" },
  { value: "Entertainment & Music", label: "Entertainment & Music" },
];

// suggestions only — the field accepts free-form tags too
const SECTION_SUGGESTIONS = [
  "HERO",
  "ABOUT",
  "FEATURES",
  "SERVICES",
  "TESTIMONIALS",
  "CONTACT",
  "GALLERY",
  "FAQ",
  "TEAM",
  "PRICING",
].map((s) => ({ value: s, label: s }));

const C = {
  border: "rgba(0,0,0,0.10)",
  text: "#1a1a1a",
  textMuted: "#6b6b6b",
  bgSurface: "#f7f6f3",
};

const emptyForm = {
  slug: "",
  name: "",
  description: "",
  thumbnailUrl: "",
  category: "",
  type: "BUSINESS",
  tags: [],
  sections: [],
  active: true,
};

export default function TemplateAdminPage() {
  const {
    templates,
    loading,
    totalElements,
    totalPages,
    fetchAll,
    createTemplate,
    updateTemplate,
    deactivateTemplate,
    reactivateTemplate,
  } = useWebsiteTemplates();

  const [saving, setSaving] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null); // null = creating
  const [form] = Form.useForm();

  // the hook appends on page > 0 (same "load more" convention as useDomains),
  // so pagination here is a simple page counter rather than antd's built-in
  // per-page table pagination
  const PAGE_SIZE = 20;
  const [page, setPage] = useState(0);
  const hasMore = page + 1 < totalPages;

  const loadMore = () => {
    if (loading || !hasMore) return;
    const next = page + 1;
    setPage(next);
    fetchAll(next, PAGE_SIZE);
  };

  const openCreate = () => {
    setEditingSlug(null);
    form.setFieldsValue(emptyForm);
    setDrawerOpen(true);
  };

  const openEdit = (record) => {
    setEditingSlug(record.slug);
    form.setFieldsValue({
      slug: record.slug,
      name: record.name,
      description: record.description,
      thumbnailUrl: record.thumbnailUrl,
      category: record.category,
      type: record.type,
      tags: record.tags || [],
      sections: record.sections || [],
      active: record.active,
    });
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editingSlug) {
        await updateTemplate(editingSlug, values);
      } else {
        await createTemplate(values);
      }
      closeDrawer();
    } catch (err) {
      if (err?.errorFields) return; // form validation error, already shown inline
      // any other failure was already toasted by the hook
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = (slug) => {
    deactivateTemplate(slug).catch(() => {
      // already toasted by the hook
    });
  };

  const handleReactivate = (slug) => {
    reactivateTemplate(slug).catch(() => {
      // already toasted by the hook
    });
  };

  const columns = [
    {
      title: "",
      dataIndex: "thumbnailUrl",
      width: 56,
      render: (url, record) => (
        <Avatar
          shape="square"
          size={40}
          src={url}
          style={{ background: C.bgSurface }}
        >
          {record.name?.[0]}
        </Avatar>
      ),
    },
    {
      title: "Template",
      dataIndex: "name",
      render: (name, record) => (
        <div>
          <div style={{ fontWeight: 600, color: C.text }}>{name}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.slug}
          </Text>
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      width: 110,
      render: (type) => (
        <Tag color={type === "PORTFOLIO" ? "purple" : "blue"}>
          {type === "PORTFOLIO" ? "Portfolio" : "Business"}
        </Tag>
      ),
      filters: TYPE_OPTIONS.map((t) => ({ text: t.label, value: t.value })),
      onFilter: (value, record) => record.type === value,
    },
    {
      title: "Category",
      dataIndex: "category",
      width: 150,
    },
    {
      title: "Tags",
      dataIndex: "tags",
      render: (tags) => (
        <Space size={[4, 4]} wrap>
          {(tags || []).map((tag) => (
            <Tag key={tag} style={{ margin: 0 }}>
              {tag}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "active",
      width: 100,
      render: (active) =>
        active ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag color="default">Inactive</Tag>
        ),
      filters: [
        { text: "Active", value: true },
        { text: "Inactive", value: false },
      ],
      onFilter: (value, record) => record.active === value,
    },
    {
      title: "",
      key: "actions",
      width: 180,
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => openEdit(record)}>
            Edit
          </Button>
          {record.active ? (
            <Popconfirm
              title="Deactivate this template?"
              description="It will be hidden from the marketplace. Existing sites built with it are unaffected."
              okText="Deactivate"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDeactivate(record.slug)}
            >
              <Button size="small" danger>
                Deactivate
              </Button>
            </Popconfirm>
          ) : (
            <Button size="small" onClick={() => handleReactivate(record.slug)}>
              Reactivate
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <div>
          <h1
            style={{ fontSize: 18, fontWeight: 600, color: C.text, margin: 0 }}
          >
            Templates
          </h1>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Create, edit, and manage the templates shown in the marketplace
          </Text>
        </div>
        <Button
          type="primary"
          onClick={openCreate}
          style={{
            background: OLIVEEB_COLORS.oliveebGreen,
            borderColor: OLIVEEB_COLORS.oliveebGreen,
          }}
        >
          New Template
        </Button>
      </div>

      <Text
        type="secondary"
        style={{ fontSize: 12, display: "block", marginBottom: 8 }}
      >
        {templates.length} of {totalElements} templates loaded
      </Text>

      <Table
        rowKey="slug"
        loading={loading}
        columns={columns}
        dataSource={templates}
        pagination={false}
        style={{
          border: `0.5px solid ${C.border}`,
          borderRadius: 10,
          overflow: "hidden",
        }}
      />

      {hasMore && (
        <div
          style={{ display: "flex", justifyContent: "center", marginTop: 16 }}
        >
          <Button onClick={loadMore} loading={loading}>
            Load more
          </Button>
        </div>
      )}

      <Drawer
        title={editingSlug ? `Edit "${editingSlug}"` : "New template"}
        open={drawerOpen}
        onClose={closeDrawer}
        size={600}
        zIndex={9999}
        extra={
          <Space>
            <Button onClick={closeDrawer}>Cancel</Button>
            <Button
              type="primary"
              loading={saving}
              onClick={handleSubmit}
              style={{
                background: OLIVEEB_COLORS.oliveebGreen,
                borderColor: OLIVEEB_COLORS.oliveebGreen,
              }}
            >
              {editingSlug ? "Save changes" : "Create template"}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" initialValues={emptyForm}>
          <Form.Item
            name="slug"
            label="Slug"
            rules={[
              { required: true, message: "Required" },
              {
                pattern: /^[a-z0-9]+(-[a-z0-9]+)*$/,
                message:
                  "Lowercase letters, numbers, and dashes only, e.g. 'start-up'",
              },
            ]}
            extra={editingSlug ? undefined : "Can't be changed after creation."}
          >
            <Input placeholder="e.g. start-up" disabled={!!editingSlug} />
          </Form.Item>

          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input placeholder="e.g. Start Up" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea
              rows={3}
              placeholder="Short description shown on the card"
            />
          </Form.Item>

          <Form.Item name="thumbnailUrl" label="Thumbnail URL">
            <Input placeholder="https://..." />
          </Form.Item>

          <Form.Item name="category" label="Category">
            <Select
              allowClear
              placeholder="Search or select a category"
              options={CATEGORY_OPTIONS}
              showSearch={{
                filter: (input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase()),
              }}
            />
          </Form.Item>

          <Form.Item
            name="type"
            label="Type"
            rules={[{ required: true, message: "Required" }]}
          >
            <Select options={TYPE_OPTIONS} />
          </Form.Item>

          <Form.Item name="tags" label="Tags">
            <Select
              mode="tags"
              placeholder="Add tags and press enter"
              tokenSeparators={[","]}
            />
          </Form.Item>

          <Form.Item name="sections" label="Sections">
            <Select
              mode="tags"
              placeholder="Add sections and press enter"
              options={SECTION_SUGGESTIONS}
              tokenSeparators={[","]}
            />
          </Form.Item>

          <Form.Item name="active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
}
